import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useAdminAuthStore } from './store/adminAuthStore';
import { useMobileStore } from './store/mobileStore';
import { useThemeStore } from './store/themeStore';
import { isMobileDevice } from './utils/deviceDetection';
import BuildInfo from './components/BuildInfo';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Home from './pages/Home';
import Calendar from './pages/Calendar';
import DesignSystem from './pages/DesignSystem';
import WeatherDesigns from './pages/WeatherDesigns';
import MobileTodoDesignProposals from './pages/MobileTodoDesignProposals';
import MobileTodoRedesignProposals from './pages/MobileTodoRedesignProposals';
import BetaTodos from './pages/mobile/BetaTodos';
import MobileTodoScaleProposals from './pages/MobileTodoScaleProposals';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import Family from './pages/Family';
import Admin from './pages/Admin';
import AdminLogin from './pages/AdminLogin';
import MobileLayout from './components/mobile/MobileLayout';
import MobileCalendar from './pages/mobile/MobileCalendar';
import QuickCreateEvent from './pages/mobile/QuickCreateEvent';
import MobileFamily from './pages/mobile/MobileFamily';
import MobileToday from './pages/mobile/MobileToday';
import TodosPage from './pages/Todos';
import WeatherPage from './pages/Weather';
import SettingsPage from './pages/Settings';
import WhatsNew from './pages/WhatsNew';
import MobileWhatsNew from './pages/mobile/MobileWhatsNew';
import Today from './pages/Today';
import Discoveries from './pages/Discoveries';
import MobileDiscoveries from './pages/mobile/MobileDiscoveries';
import MobileAccount from './pages/mobile/MobileAccount';
import MobilePreview from './pages/MobilePreview';
import ToastContainer from './components/ToastContainer';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function AdminProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAdminAuthStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/admin/login" replace />;
}

function MobileRoute({ children }: { children: React.ReactNode }) {
  const { isMobile, updateDeviceInfo } = useMobileStore();
  const location = useLocation();
  
  // Check device directly using user agent detection
  // This state updates on window resize to catch DevTools device simulation
  const [detectedMobile, setDetectedMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return isMobileDevice();
  });
  
  useEffect(() => {
    updateDeviceInfo();
    
    const checkMobile = () => {
      const mobile = isMobileDevice();
      setDetectedMobile(mobile);
      updateDeviceInfo();
    };
    
    // Check immediately
    checkMobile();
    
    // Listen for resize events (catches DevTools device simulation)
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, [updateDeviceInfo]);
  
  const shouldUseMobile = isMobile || detectedMobile;
  
  // If on mobile and trying to access desktop route, redirect to mobile equivalent
  if (shouldUseMobile && !location.pathname.startsWith('/mobile')) {
    if (location.pathname === '/' || location.pathname === '/home') {
      return <Navigate to="/mobile/calendar" replace />;
    }
    if (location.pathname === '/family') {
      return <Navigate to="/mobile/family" replace />;
    }
    if (location.pathname === '/todos') {
      return <Navigate to="/mobile/todos" replace />;
    }
    if (location.pathname === '/discoveries') {
      return <Navigate to="/mobile/discoveries" replace />;
    }
    if (location.pathname === '/whats-new') {
      return <Navigate to="/mobile/whats-new" replace />;
    }
    if (location.pathname === '/settings' || location.pathname === '/me') {
      return <Navigate to="/mobile/me" replace />;
    }
  }
  
  return <>{children}</>;
}

function DesktopRoute({ children }: { children: React.ReactNode }) {
  const { isMobile, updateDeviceInfo } = useMobileStore();
  const location = useLocation();
  
  // Check device directly using user agent detection
  // This state updates on window resize to catch DevTools device simulation
  const [detectedMobile, setDetectedMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return isMobileDevice();
  });
  
  useEffect(() => {
    updateDeviceInfo();
    
    const checkMobile = () => {
      const mobile = isMobileDevice();
      setDetectedMobile(mobile);
      updateDeviceInfo();
    };
    
    // Check immediately
    checkMobile();
    
    // Listen for resize events (catches DevTools device simulation)
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, [updateDeviceInfo]);
  
  const shouldUseMobile = isMobile || detectedMobile;
  
  // If on mobile device, redirect to mobile routes
  if (shouldUseMobile && !location.pathname.startsWith('/mobile')) {
    if (location.pathname === '/' || location.pathname === '/home') {
      return <Navigate to="/mobile/calendar" replace />;
    }
    if (location.pathname === '/family') {
      return <Navigate to="/mobile/family" replace />;
    }
    if (location.pathname === '/todos') {
      return <Navigate to="/mobile/todos" replace />;
    }
    if (location.pathname === '/discoveries') {
      return <Navigate to="/mobile/discoveries" replace />;
    }
    if (location.pathname === '/whats-new') {
      return <Navigate to="/mobile/whats-new" replace />;
    }
    if (location.pathname === '/settings' || location.pathname === '/me') {
      return <Navigate to="/mobile/me" replace />;
    }
  }
  
  // If on desktop and trying to access mobile route, redirect to desktop equivalent
  if (!shouldUseMobile && location.pathname.startsWith('/mobile')) {
    if (location.pathname === '/mobile/calendar') {
      return <Navigate to="/" replace />;
    }
    if (location.pathname === '/mobile/family') {
      return <Navigate to="/family" replace />;
    }
    if (location.pathname === '/mobile/whats-new') {
      return <Navigate to="/whats-new" replace />;
    }
    if (location.pathname === '/mobile/me') {
      return <Navigate to="/settings" replace />;
    }
  }
  
  // Desktop: /me → /settings
  if (!shouldUseMobile && location.pathname === '/me') {
    return <Navigate to="/settings" replace />;
  }
  
  return <>{children}</>;
}

function App() {
  const { initializeTokenRefresh } = useAuthStore();
  const { updateDeviceInfo } = useMobileStore();
  const { isDarkMode } = useThemeStore();

  // Initialize token refresh mechanism on app startup
  useEffect(() => {
    initializeTokenRefresh();
  }, [initializeTokenRefresh]);

  // Initialize device detection
  useEffect(() => {
    updateDeviceInfo();
  }, [updateDeviceInfo]);

  // Sync theme with HTML data-theme attribute
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const html = document.documentElement;
      html.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    }
  }, [isDarkMode]);

  return (
    <Router>
      <BuildInfo />
      <ToastContainer />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/design" element={<DesignSystem />} />
        <Route path="/designs" element={<DesignSystem />} />
        <Route path="/design/weather" element={<WeatherDesigns />} />
        <Route path="/designs/weather" element={<WeatherDesigns />} />
        <Route path="/weather/design" element={<WeatherDesigns />} />
        <Route path="/design/mobile-todos" element={<MobileTodoDesignProposals />} />
        <Route path="/designs/mobile-todos" element={<MobileTodoDesignProposals />} />
        <Route path="/design/mobile-todos-redesign" element={<MobileTodoRedesignProposals />} />
        <Route path="/designs/mobile-todos-redesign" element={<MobileTodoRedesignProposals />} />
        <Route path="/design/mobile-todos-scale" element={<MobileTodoScaleProposals />} />
        <Route path="/designs/mobile-todos-scale" element={<MobileTodoScaleProposals />} />
        <Route
          path="/design/mobile-preview"
          element={
            <ProtectedRoute>
              <DesktopRoute>
                <MobilePreview />
              </DesktopRoute>
            </ProtectedRoute>
          }
        />
        
        {/* Protected desktop routes */}
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <ChangePassword />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DesktopRoute>
                <Calendar />
              </DesktopRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <DesktopRoute>
                <Home />
              </DesktopRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/todos"
          element={
            <ProtectedRoute>
              <DesktopRoute>
                <TodosPage />
              </DesktopRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/weather"
          element={
            <ProtectedRoute>
              <DesktopRoute>
                <WeatherPage />
              </DesktopRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <DesktopRoute>
                <SettingsPage />
              </DesktopRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/whats-new"
          element={
            <ProtectedRoute>
              <DesktopRoute>
                <WhatsNew />
              </DesktopRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/today"
          element={
            <ProtectedRoute>
              <DesktopRoute>
                <Today />
              </DesktopRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/discoveries"
          element={
            <ProtectedRoute>
              <DesktopRoute>
                <Discoveries />
              </DesktopRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/family"
          element={
            <ProtectedRoute>
              <DesktopRoute>
                <Family />
              </DesktopRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminProtectedRoute>
              <DesktopRoute>
                <Admin />
              </DesktopRoute>
            </AdminProtectedRoute>
          }
        />
        
        {/* Protected mobile routes */}
        <Route
          path="/mobile"
          element={
            <ProtectedRoute>
              <MobileRoute>
                <Navigate to="/mobile/calendar" replace />
              </MobileRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/mobile/today"
          element={
            <ProtectedRoute>
              <MobileRoute>
                <MobileLayout>
                  <MobileToday />
                </MobileLayout>
              </MobileRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/mobile/calendar"
          element={
            <ProtectedRoute>
              <MobileRoute>
                <MobileLayout>
                  <MobileCalendar />
                </MobileLayout>
              </MobileRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/mobile/todos"
          element={
            <ProtectedRoute>
              <MobileRoute>
                <MobileLayout>
                  <BetaTodos />
                </MobileLayout>
              </MobileRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/mobile/create"
          element={
            <ProtectedRoute>
              <MobileRoute>
                <MobileLayout>
                  <QuickCreateEvent />
                </MobileLayout>
              </MobileRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/mobile/family"
          element={
            <ProtectedRoute>
              <MobileRoute>
                <MobileLayout>
                  <MobileFamily />
                </MobileLayout>
              </MobileRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/mobile/whats-new"
          element={
            <ProtectedRoute>
              <MobileRoute>
                <MobileLayout>
                  <MobileWhatsNew />
                </MobileLayout>
              </MobileRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/mobile/discoveries"
          element={
            <ProtectedRoute>
              <MobileRoute>
                <MobileLayout>
                  <MobileDiscoveries />
                </MobileLayout>
              </MobileRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/mobile/me"
          element={
            <ProtectedRoute>
              <MobileRoute>
                <MobileLayout>
                  <MobileAccount />
                </MobileLayout>
              </MobileRoute>
            </ProtectedRoute>
          }
        />
        
        {/* Catch-all: redirect unknown routes to home, but preserve /admin */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
